import { Platform } from 'react-native';

import {
  useInitPermissionsMachine,
  usePermissionsMachine,
} from '@sg/shared-fe-features-permissions';

import { type FeatureFlagTypes, useFeatureFlag } from '@order/LaunchDarkly';

// ─────────────────────────────────────────────────────────────────────────────

export const useBackgroundLocationPermissionPrimer = (params: Params) => {
  const { variants } = params;

  const isFeatureEnabled = useFeatureFlag(
    'CELS-5610-order-background-location-permission-primer-enabled',
  );
  const placementVariant = useFeatureFlag('CELS-5256-geofencing-primer-variant');

  // ─── Derived ─────────────────────────────────────────────────────────

  const isPlacementEnabled = placementVariant !== 'disabled' && variants.includes(placementVariant);
  const isBackgroundLocationPrimerEnabled =
    Platform.OS !== 'web' && isFeatureEnabled && isPlacementEnabled;

  // ─── Actor ───────────────────────────────────────────────────────────

  const permissionsActorRef = useInitPermissionsMachine({ isBackgroundLocationPrimerEnabled });

  const {
    isBackgroundLocationPermissionsLoading,
    isBackgroundLocationPrimerVisible,
    isRequestingBackgroundLocationPermission,
    isSkippingBackgroundLocationPermission,
    isBackgroundLocationPermissionApproved,
    isBackgroundLocationPermissionDenied,
    isBackgroundLocationPermissionSkipped,
    isBackgroundLocationPermissionAlreadyShown,
    requestBackgroundLocationPermissions,
    skipBackgroundLocationPermissions,
  } = usePermissionsMachine(permissionsActorRef);

  // ─── Derived ─────────────────────────────────────────────────────────

  const isPrimerActive =
    isBackgroundLocationPrimerVisible ||
    isRequestingBackgroundLocationPermission ||
    isSkippingBackgroundLocationPermission;

  // ─────────────────────────────────────────────────────────────────────

  return {
    isBackgroundLocationPrimerEnabled,
    isBackgroundLocationPermissionsLoading,
    isPrimerActive,
    isRequestingBackgroundLocationPermission,
    isSkippingBackgroundLocationPermission,
    isBackgroundLocationPermissionApproved,
    isBackgroundLocationPermissionDenied,
    isBackgroundLocationPermissionSkipped,
    isBackgroundLocationPermissionAlreadyShown,
    onAllow: requestBackgroundLocationPermissions,
    onSkip: skipBackgroundLocationPermissions,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  variants: Exclude<FeatureFlagTypes.LocationPrimerVariation, 'disabled'>[];
};
